import { UseModel } from 'core';
import useGetSuggestions from 'domain/selectors/dst/useGetSuggestions';
import { useDSTContext, useDSTActionContext } from 'presentation/components/pages/CMPage/DSTContext';
import { dispatchGAEvent } from '@uds/tagmg';
import { useHistory } from 'react-router';
import { MutableRefObject, RefObject, useRef } from 'react';
import useIntersectionObserver from 'presentation/hooks/useIntersectionObserver';
import { useUpdateQueryParam } from 'domain/selectors/common';
import { useBasketCount } from '@uds/bookends';

import { DomainSearchFormValues } from './types';

type Model = {
  domainName: string;
  isError: boolean;
  isSticky: boolean;
  ref: RefObject<HTMLSpanElement>;
  basketCount: number;
};
type Operations = {
  onSubmit: (values: DomainSearchFormValues) => void;
};
type UseDomainSearchInput = UseModel<Model, Operations>;

type Props = {
  analytics: string | undefined;
  redirect: boolean;
  destination: string;
  tld?: string;
};

const scrollCallback = (ref: MutableRefObject<HTMLSpanElement | null>, redirect: boolean) => {
  if (!redirect) {
    const bodyTop = document.body.getBoundingClientRect().top;
    const dstTop = ref.current?.getBoundingClientRect().top;
    if (dstTop) {
      window.scrollTo({ top: dstTop - bodyTop });
    }
  }
};

export default function useDomainSearchInput({ analytics, redirect, destination, tld }: Props): UseDomainSearchInput {
  const ref = useRef<HTMLSpanElement>(null);
  const { data: basketCount } = useBasketCount();
  const entry = useIntersectionObserver(ref, {});
  const isSticky =
    entry != null &&
    !entry.isIntersecting && // It is not in the viewport
    entry.boundingClientRect.y < 0 && // we actually scrolled passed it
    redirect !== true; // we are on DST 2.0 page

  const { refetch, isError } = useGetSuggestions();
  const history = useHistory();
  const updateQueryParam = useUpdateQueryParam();

  const { domainName } = useDSTContext();
  const setQuery = useDSTActionContext();

  const tldSuffix = tld ?? '';

  const updateSearch = (values: DomainSearchFormValues) => {
    // Do not append more than once the tld suffix
    const searchTerm = values.domainSearchTerm.endsWith(tldSuffix + tldSuffix)
      ? values.domainSearchTerm
      : values.domainSearchTerm + tldSuffix;
    setQuery({ type: 'SET_QUERY', payload: { domainName: searchTerm } });
    updateQueryParam({ param: 'domain', value: searchTerm });

    dispatchGAEvent({
      hitType: 'event',
      eventCategory: 'USF Interaction',
      eventAction: 'form submit',
      eventLabel: analytics ?? 'DST v.2.0 Search Submit',
    });
  };

  const onSubmit = (values: DomainSearchFormValues) => {
    // we want domain names to have at least length 3
    if (values.domainSearchTerm.length < 3) {
      return;
    }
    if (redirect) {
      history.push(`${destination}?domain=${values.domainSearchTerm + tldSuffix}`);
      return;
    }

    if (values.domainSearchTerm === domainName && isError) {
      // This will allow the user to refetch when we have an error
      refetch();
    } else {
      scrollCallback(ref, redirect);
      // trick code into waiting for the scroll before triggering the search.
      // Apparently its not instant enough otherwise.
      setTimeout(() => updateSearch(values), 0);
    }
  };
  return {
    models: {
      isError,
      domainName,
      isSticky,
      ref,
      basketCount,
    },
    operations: {
      onSubmit,
    },
  };
}
